import { Injectable, Inject, Optional } from '@angular/core';
import { LangFuseService } from './langfuse.service';
import {
  IQ_BOOST_LANGFUSE,
  IQ_BOOST_LANGFUSE_SLE,
  MAXIMIZER_AI_ENVIRONMENT,
} from '../types/injection-token';
import {
  LANGFUSE_PROMPT_TAG,
  LANGFUSE_SYSTEM_TAG,
} from '../constants/langfuse.constants';
import { TextPromptClient } from 'langfuse';
import { TextPrompt } from 'langfuse-core';
import {
  LangfuseResponseExtension,
  RequestApiPromptMeta,
  ResponseTextPrompt,
  EnvironmentType,
  LanguageHelper,
} from '../classes/iqBoost';
import { forkJoin, Observable, of, switchMap, map, tap } from 'rxjs';
import { IQBoostViewStore } from '../state/iqboost-view.store';
import { OpenAI } from '@maximizer/core/shared/domain';
import { v4 as uuidv4 } from 'uuid';
import { PromptMetaExtended } from '../types/langfuse.types';
import { AiFeaturesService } from './ai-features.service';

export enum SystemType {
  CREATE_CRM_OBJECT = 'create-crm-object',
  CREATE_REFERENCE = 'create-reference',
  ANONYMIZED_CONSIDERATIONS = 'anonymized-considerations',
  DETAILED_REPORT_INSTRUCTIONS = 'detailed-report-instructions',
  SMART_SEARCH = 'smart-search',
  SUGGESTED_PROMPTS = 'suggested-prompts',
  CHANGE_LANGUAGE = 'change-language',
  WEB_SEARCH = 'web-search',
}

@Injectable()
export class LangfuseManagerService {

  private readonly promptCache = new Map<string, TextPromptClient>();

  constructor(
    @Optional() @Inject(IQ_BOOST_LANGFUSE) private readonly defaultLangfuse: LangFuseService | null,
    @Optional() @Inject(IQ_BOOST_LANGFUSE_SLE) private readonly sleLangfuse: LangFuseService | null,
    @Inject(MAXIMIZER_AI_ENVIRONMENT) private readonly environment: EnvironmentType,
    private readonly store: IQBoostViewStore,
    private readonly aiFeatures: AiFeaturesService
  ) { }

  getProductionPrompts(customLangfuseService?: LangFuseService): Observable<PromptMetaExtended[]> {
    const service = customLangfuseService ?? this.getNativeLangfuseService();
    return service
      .listAllPrompts({ tag: LANGFUSE_PROMPT_TAG, label: this.getEnvironmentLabel(), limit: 100 })
      .pipe(
        map((prompts) => prompts.map(promptMeta => ({ id: uuidv4(), promptMeta }))),
      );
  }

  /** System prompts from Langfuse; Outlook loads these client-side, AI uses the lookup API. */
  getSystemPrompts(): Observable<PromptMetaExtended[]> {
    const environmentLabel = this.getEnvironmentLabel();

    return this.getNativeLangfuseService()
      .listAllPrompts({ tag: LANGFUSE_SYSTEM_TAG, limit: 100 })
      .pipe(
        map((prompts) => prompts.map(promptMeta => ({ id: uuidv4(), promptMeta }))),
        switchMap((prompts) => {
          const requestPrompts = prompts
            .filter((promptMeta) => promptMeta.promptMeta.labels.includes(environmentLabel))
            .map((promptMeta) => this.getSystemPrompt(
              { promptMeta, askAi: true },
              environmentLabel,
            ));

          if (!requestPrompts.length) {
            this.store.setSystemPrompts([]);
            return of(prompts);
          }

          return forkJoin(requestPrompts)
            .pipe(
              tap((resolved) =>
                this.store.setSystemPrompts(resolved.map(prompt => prompt.textPromptClient.promptResponse))
              ),
              map(() => prompts),
            );
        })
      );
  }

  getSystemPromptByName(type: SystemType): Observable<TextPrompt | undefined> {
    const environmentLabel = this.getEnvironmentLabel();

    return this.getNativeLangfuseService()
      .listAllPrompts({ tag: LANGFUSE_SYSTEM_TAG, limit: 100, label: environmentLabel })
      .pipe(
        switchMap((prompts) => {
          const promptMeta = prompts.find(promptMeta => (promptMeta.lastConfig as LangfuseResponseExtension)?.maximizer?.systemType === type);
          if (promptMeta) {
            return this.getSystemPrompt(
              {
                promptMeta: { id: uuidv4(), promptMeta } as PromptMetaExtended,
                askAi: true
              },
              environmentLabel
            );
          }
          return of(undefined);
        }),
        map((prompt) => prompt?.textPromptClient.promptResponse),
      );
  }

  getPrompt(
    requestPromptMeta: RequestApiPromptMeta,
    label?: string,
    fallback = '',
  ): Observable<ResponseTextPrompt> {
    const { promptMeta } = requestPromptMeta;
    const promptName = promptMeta.promptMeta.name;
    const effectiveLabel = label ?? this.getEnvironmentLabel();
    const cacheKey = this.buildPromptCacheKey(promptName, effectiveLabel);

    const cached = this.promptCache.get(cacheKey);
    if (cached) {
      return of(this.toResponseTextPrompt(requestPromptMeta, cached));
    }

    return this.getNativeLangfuseService().getTextPrompt(promptName, fallback, effectiveLabel).pipe(
      tap((client) => this.promptCache.set(cacheKey, client)),
      map((client) => this.toResponseTextPrompt(requestPromptMeta, client)),
    );
  }

  private getSystemPrompt(
    requestPromptMeta: RequestApiPromptMeta,
    label?: string,
    fallback = '',
  ): Observable<ResponseTextPrompt> {
    const { promptMeta } = requestPromptMeta;
    const promptName = promptMeta.promptMeta.name;
    const effectiveLabel = label ?? this.getEnvironmentLabel();
    const cacheKey = this.buildPromptCacheKey(`system::${promptName}`, effectiveLabel);

    const cached = this.promptCache.get(cacheKey);
    if (cached) {
      return of(this.toResponseTextPrompt(requestPromptMeta, cached));
    }

    return this.getNativeLangfuseService().getTextPrompt(promptName, fallback, effectiveLabel).pipe(
      tap((client) => this.promptCache.set(cacheKey, client)),
      map((client) => this.toResponseTextPrompt(requestPromptMeta, client)),
    );
  }

  private buildPromptCacheKey(promptName: string, label: string | undefined): string {
    return `${promptName}::${label}`;
  }

  private toResponseTextPrompt(
    request: RequestApiPromptMeta,
    client: TextPromptClient,
  ): ResponseTextPrompt {
    return { textPromptClient: client, request };
  }

  getNativeLangfuseService(): LangFuseService {
    const service = this.aiFeatures.isSLE() ? this.sleLangfuse : this.defaultLangfuse;
    if (!service) {
      throw new Error(
        'LangfuseManagerService requires IQ_BOOST_LANGFUSE or IQ_BOOST_LANGFUSE_SLE to be provided',
      );
    }
    return service;
  }

  getInstructionsForSummary(currentPrompt: TextPromptClient | undefined): OpenAI.GPTMessage[] {
    const instructionsPrompts = [];
    const currentPromptConfig = currentPrompt?.config as LangfuseResponseExtension;

    if (currentPromptConfig?.maximizer?.priviliges?.create) {
      const instruction = this.findSystemPromptByType(SystemType.CREATE_CRM_OBJECT);
      if (instruction) {
        instructionsPrompts.push(instruction);
      }
    }

    if (currentPromptConfig?.maximizer?.priviliges?.references) {
      const instruction = this.findSystemPromptByType(SystemType.CREATE_REFERENCE);
      if (instruction) {
        instructionsPrompts.push(instruction);
      }
    }

    return instructionsPrompts.map(prompt => LangfuseManagerService.convertToSystemPrompt(prompt.prompt));
  }

  getInstructionForChangeLanguage(): TextPrompt | undefined {
    return this.findSystemPromptByType(SystemType.CHANGE_LANGUAGE);
  }

  private findSystemPromptByType(systemType: SystemType): TextPrompt | undefined {
    return this.store.systemPrompts().find(prompt => {
      const config = prompt?.config as LangfuseResponseExtension;
      return config?.maximizer?.systemType === systemType;
    });
  }

  getEnvironmentLabel(): EnvironmentType {
    return this.environment;
  }

  static convertToSystemPrompt(prompt: string): OpenAI.GPTMessage {
    return { role: 'system', content: prompt };
  }

  static compilePrompt(
    prompt: TextPromptClient | undefined,
    otherVariables: Record<string, string> = {},
    language = LanguageHelper.language(),
  ): string {
    if (!prompt) {
      return '';
    }

    const flattenedVariables: Record<string, string> = {
      ...otherVariables,
    };
    const config = prompt.config as LangfuseResponseExtension | undefined;

    if (config?.variables) {
      Object.entries(config.variables).forEach(([key, value]) => {
        if (value.translationValue) {
          flattenedVariables[key] = value.translationValue[language];
        }
      });
    }

    return prompt.compile(flattenedVariables);
  }
}
